"use client";
import { FocusTrap } from "../focus-trap/focus-trap.js";
import { PortalContext } from "./portal-context.js";
import { createElement, createHook, forwardRef, setRef, useLiveRef, useMergeRefs, useSafeLayoutEffect, useWrapElement } from "@ariakit/react-utils";
import { disableFocusIn, getDocument, getNextTabbable, getPreviousTabbable, isFocusEventOutside, restoreFocusIn } from "@ariakit/utils";
import { useContext, useEffect, useRef, useState } from "react";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
import { createPortal } from "react-dom";
//#region src/portal/portal.tsx
const TagName = "div";
function getRootElement(element) {
	const doc = getDocument(element);
	const { fullscreenElement } = doc;
	const HTMLElementClass = doc.defaultView?.HTMLElement;
	if (HTMLElementClass && fullscreenElement instanceof HTMLElementClass) return fullscreenElement;
	return doc.body;
}
function getPortalElement(element, portalElement) {
	if (!portalElement) return getDocument(element).createElement("div");
	if (typeof portalElement === "function") return portalElement(element);
	return portalElement;
}
function getRandomId(prefix = "id") {
	return `${prefix ? `${prefix}-` : ""}${Math.random().toString(36).slice(2, 8)}`;
}
function queueFocus(element) {
	queueMicrotask(() => {
		element?.focus();
	});
}
function attachPortalRef(ref, node) {
	return {
		ref,
		node,
		cleanup: setRef(ref, node)
	};
}
function detachPortalRef(attached) {
	if (typeof attached.cleanup === "function") attached.cleanup();
	else setRef(attached.ref, null);
}
/**
* Returns props to create a `Portal` component.
* @see https://ariakit.com/components/portal
* @example
* ```jsx
* const props = usePortal();
* <Role {...props}>Content</Role>
* ```
*/
const usePortal = createHook(function usePortal({ preserveTabOrder, preserveTabOrderAnchor, portalElement, portalRef, portal = true, ...props }) {
	const ref = useRef(null);
	const refProp = useMergeRefs(ref, props.ref);
	const context = useContext(PortalContext);
	const [portalNode, setPortalNode] = useState(null);
	const [anchorPortalNode, setAnchorPortalNode] = useState(null);
	const outerBeforeRef = useRef(null);
	const innerBeforeRef = useRef(null);
	const innerAfterRef = useRef(null);
	const outerAfterRef = useRef(null);
	const portalRefProp = useLiveRef(portalRef);
	const attachedPortalRefRef = useRef(null);
	useSafeLayoutEffect(() => {
		const element = ref.current;
		if (!element || !portal) {
			setPortalNode(null);
			return;
		}
		const portalEl = getPortalElement(element, portalElement);
		if (!portalEl) {
			setPortalNode(null);
			return;
		}
		const isPortalInDocument = portalEl.isConnected;
		if (!isPortalInDocument) (context || getRootElement(element)).appendChild(portalEl);
		if (!portalEl.id) portalEl.id = element.id ? `portal/${element.id}` : getRandomId();
		setPortalNode(portalEl);
		attachedPortalRefRef.current = attachPortalRef(portalRefProp.current, portalEl);
		return () => {
			const attached = attachedPortalRefRef.current;
			if (attached) {
				attachedPortalRefRef.current = null;
				detachPortalRef(attached);
			}
			if (!isPortalInDocument) portalEl.remove();
		};
	}, [
		portal,
		portalElement,
		context
	]);
	useSafeLayoutEffect(() => {
		const attached = attachedPortalRefRef.current;
		if (!attached) return;
		if (attached.ref === portalRef) return;
		detachPortalRef(attached);
		attachedPortalRefRef.current = attachPortalRef(portalRef, attached.node);
	}, [portalRef]);
	useEffect(() => {
		if (!portalNode) return;
		if (context) return;
		if (portalElement) return;
		const doc = getDocument(portalNode);
		const onFullscreenChange = () => {
			const rootElement = getRootElement(portalNode);
			if (portalNode.parentElement !== rootElement) rootElement.appendChild(portalNode);
		};
		if (portalNode.isConnected) onFullscreenChange();
		doc.addEventListener("fullscreenchange", onFullscreenChange);
		return () => {
			doc.removeEventListener("fullscreenchange", onFullscreenChange);
		};
	}, [
		portalNode,
		context,
		portalElement
	]);
	useSafeLayoutEffect(() => {
		if (!portal) return;
		if (!preserveTabOrder) return;
		if (!preserveTabOrderAnchor) return;
		const element = getDocument(preserveTabOrderAnchor).createElement("span");
		element.style.position = "fixed";
		preserveTabOrderAnchor.insertAdjacentElement("afterend", element);
		setAnchorPortalNode(element);
		return () => {
			element.remove();
			setAnchorPortalNode(null);
		};
	}, [
		portal,
		preserveTabOrder,
		preserveTabOrderAnchor
	]);
	useEffect(() => {
		if (!portalNode) return;
		if (!preserveTabOrder) return;
		let raf = 0;
		const onFocus = (event) => {
			if (!isFocusEventOutside(event)) return;
			const focusing = event.type === "focusin";
			cancelAnimationFrame(raf);
			if (focusing) return restoreFocusIn(portalNode);
			raf = requestAnimationFrame(() => {
				disableFocusIn(portalNode, true);
			});
		};
		portalNode.addEventListener("focusin", onFocus, true);
		portalNode.addEventListener("focusout", onFocus, true);
		return () => {
			cancelAnimationFrame(raf);
			portalNode.removeEventListener("focusin", onFocus, true);
			portalNode.removeEventListener("focusout", onFocus, true);
		};
	}, [portalNode, preserveTabOrder]);
	props = useWrapElement(props, (element) => {
		element = /* @__PURE__ */ jsx(PortalContext.Provider, {
			value: portalNode || context,
			children: element
		});
		if (!portal) return element;
		if (!portalNode) return /* @__PURE__ */ jsx("span", {
			ref: refProp,
			id: props.id,
			style: { position: "fixed" },
			hidden: true
		});
		element = /* @__PURE__ */ jsxs(Fragment$1, { children: [
			preserveTabOrder && /* @__PURE__ */ jsx(FocusTrap, {
				ref: innerBeforeRef,
				"data-focus-trap": props.id,
				className: "__focus-trap-inner-before",
				onFocus: (event) => {
					if (isFocusEventOutside(event, portalNode)) queueFocus(getNextTabbable());
					else queueFocus(outerBeforeRef.current);
				}
			}),
			element,
			preserveTabOrder && /* @__PURE__ */ jsx(FocusTrap, {
				ref: innerAfterRef,
				"data-focus-trap": props.id,
				className: "__focus-trap-inner-after",
				onFocus: (event) => {
					if (isFocusEventOutside(event, portalNode)) queueFocus(getPreviousTabbable());
					else queueFocus(outerAfterRef.current);
				}
			})
		] });
		element = createPortal(element, portalNode);
		let preserveTabOrderElement = /* @__PURE__ */ jsxs(Fragment$1, { children: [
			preserveTabOrder && /* @__PURE__ */ jsx(FocusTrap, {
				ref: outerBeforeRef,
				"data-focus-trap": props.id,
				className: "__focus-trap-outer-before",
				onFocus: (event) => {
					if (!(event.relatedTarget === outerAfterRef.current) && isFocusEventOutside(event, portalNode)) queueFocus(innerBeforeRef.current);
					else queueFocus(getPreviousTabbable());
				}
			}),
			preserveTabOrder && /* @__PURE__ */ jsx("span", {
				"aria-owns": portalNode.id,
				style: { position: "fixed" }
			}),
			preserveTabOrder && /* @__PURE__ */ jsx(FocusTrap, {
				ref: outerAfterRef,
				"data-focus-trap": props.id,
				className: "__focus-trap-outer-after",
				onFocus: (event) => {
					if (isFocusEventOutside(event, portalNode)) queueFocus(innerAfterRef.current);
					else {
						const nextTabbable = getNextTabbable();
						if (nextTabbable === innerBeforeRef.current) {
							requestAnimationFrame(() => getNextTabbable()?.focus());
							return;
						}
						queueFocus(nextTabbable);
					}
				}
			})
		] });
		if (anchorPortalNode && preserveTabOrder) preserveTabOrderElement = createPortal(preserveTabOrderElement, anchorPortalNode);
		return /* @__PURE__ */ jsxs(Fragment$1, { children: [preserveTabOrderElement, element] });
	}, [
		portalNode,
		context,
		portal,
		props.id,
		preserveTabOrder,
		anchorPortalNode
	]);
	props = {
		...props,
		ref: refProp
	};
	return props;
});
/**
* Renders an element using [React
* Portal](https://react.dev/reference/react-dom/createPortal).
*
* By default, the portal element is a `div` element appended to the
* `document.body` element. You can customize this with the
* [`portalElement`](https://ariakit.com/reference/portal#portalelement) prop.
*
* The
* [`preserveTabOrder`](https://ariakit.com/reference/portal#preservetaborder)
* prop allows this component to manage the tab order of the elements. It
* ensures the tab order remains consistent with the original location where the
* portal was rendered in the React tree, instead of the final location in the
* DOM. The
* [`preserveTabOrderAnchor`](https://ariakit.com/reference/portal#preservetaborderanchor)
* prop can specify a different location from which the tab order is preserved.
* @see https://ariakit.com/components/portal
* @example
* ```jsx
* <Portal>Content</Portal>
* ```
*/
const Portal = forwardRef(function Portal(props) {
	return createElement(TagName, usePortal(props));
});
//#endregion
export { Portal, usePortal };

//# sourceMappingURL=portal.js.map